<template>
    <b-modal
        :id="modalId"
        v-model="visible"
        title="Manage Ingredient Presets"
        size="xl"
        @show="onShow"
        @hidden="onHidden"
    >
        <b-overlay :show="loading" rounded="sm">
            <!-- Preset List View -->
            <div v-if="!editingPreset">
                <b-button variant="primary" class="mb-3" @click="onNewPreset">
                    <i class="fas fa-plus"></i> New Preset
                </b-button>

                <b-table
                    v-if="presets.length > 0"
                    :items="presets"
                    :fields="presetFields"
                    striped hover small
                >
                    <template #cell(name)="data">
                        <strong>{{ data.item.name }}</strong>
                        <b-badge v-if="data.item.isDefault" variant="success" class="ml-1">Default</b-badge>
                    </template>
                    <template #cell(ingredientCount)="data">
                        {{ (data.item.ingredients || []).length }}
                    </template>
                    <template #cell(actions)="data">
                        <b-button size="sm" variant="info" class="mr-1" @click="onEditPreset(data.item)">
                            <i class="fas fa-edit"></i> Edit
                        </b-button>
                        <b-button size="sm" variant="danger" @click="onDeletePreset(data.item)">
                            <i class="fas fa-trash"></i>
                        </b-button>
                    </template>
                </b-table>

                <b-alert v-else show variant="info">
                    No presets configured. Create a preset to save named ingredient combinations.
                </b-alert>
            </div>

            <!-- Preset Edit Form -->
            <div v-if="editingPreset">
                <b-button variant="outline-secondary" size="sm" class="mb-3" @click="cancelEdit">
                    <i class="fas fa-arrow-left"></i> Back to List
                </b-button>

                <b-row class="mb-3">
                    <b-col cols="6">
                        <fg-input v-model="editPreset.name" label="Preset Name" placeholder="e.g., Summer Formula"
                            :class="{ 'has-danger': isNameDuplicate }" />
                        <small v-if="isNameDuplicate" class="text-danger">
                            A preset with this name already exists for this compound.
                        </small>
                    </b-col>
                    <b-col cols="3" class="pt-4">
                        <b-form-checkbox v-model="editPreset.isDefault">
                            Default Preset
                        </b-form-checkbox>
                    </b-col>
                </b-row>

                <h6>Ingredient Selections</h6>
                <b-alert show variant="info" class="mb-2">
                    <small>
                        <i class="fas fa-info-circle"></i>
                        For each ingredient slot, select the drug to use. Only equivalent drugs are shown as options.
                    </small>
                </b-alert>

                <b-table-simple hover small>
                    <b-thead>
                        <b-tr>
                            <b-th>Formula Ingredient</b-th>
                            <b-th>Selected Drug</b-th>
                            <b-th class="text-right">Quantity</b-th>
                            <b-th>UoM</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="slot in ingredientSlots" :key="slot.ingredientId">
                            <b-td>
                                <template v-if="slot.loadingDrugName">
                                    <i class="fas fa-spinner fa-pulse text-muted"></i>
                                    <small class="text-muted ml-1">Loading...</small>
                                </template>
                                <template v-else>
                                    <strong>{{ slot.originalDrugName }}</strong>
                                    <br><small class="text-muted">{{ drugSourceLabel(slot.originalDrugSource) }}</small>
                                </template>
                            </b-td>
                            <b-td>
                                <template v-if="slot.loadingEquivalents">
                                    <b-form-select
                                        v-model="slot.selectedDrugKey"
                                        size="sm"
                                        disabled
                                    >
                                        <option :value="slot.selectedDrugKey">Loading equivalents...</option>
                                    </b-form-select>
                                    <small class="text-muted"><i class="fas fa-spinner fa-pulse"></i> Loading equivalents...</small>
                                </template>
                                <template v-else>
                                    <b-form-select
                                        v-model="slot.selectedDrugKey"
                                        size="sm"
                                        @change="onSlotDrugChanged(slot)"
                                    >
                                        <option :value="drugKey(slot.originalDrugSource, slot.originalDrugId)">
                                            {{ slot.originalDrugName }} (Original)
                                        </option>
                                        <option
                                            v-for="eq in slot.equivalents"
                                            :key="drugKey(eq.drugSource, eq.drugID)"
                                            :value="drugKey(eq.drugSource, eq.drugID)"
                                        >
                                            {{ eq.name }}
                                        </option>
                                    </b-form-select>
                                </template>
                            </b-td>
                            <b-td class="text-right">
                                {{ slot.quantity }}
                            </b-td>
                            <b-td>
                                {{ slot.unitOfMeasure }}
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <b-row class="mt-3">
                    <b-col>
                        <b-button variant="success" @click="savePreset" :disabled="!canSave || saving">
                            <i class="fas fa-save"></i> {{ editPreset.id ? 'Update' : 'Create' }} Preset
                        </b-button>
                        <b-button variant="secondary" class="ml-2" @click="cancelEdit">
                            Cancel
                        </b-button>
                    </b-col>
                </b-row>
            </div>
        </b-overlay>

        <template #modal-footer="{ hide }">
            <b-button variant="secondary" @click="hide()">
                Close
            </b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { CompoundDrug, CompoundIngredients, CompoundPreset, CompoundPresetIngredient } from '@/models/Drug/CompoundDrug';
import { DrugProduct, DrugSource } from '@/models/Drug/DrugProduct';
import { DrugStatus } from '@/models/Drug/Drug';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

const statusIndicators: Record<number, string> = {
    [DrugStatus.DrugStatusEnum.ActiveAndPreferred]: '\u2B50',
    [DrugStatus.DrugStatusEnum.Active]: '\uD83D\uDFE2',
    [DrugStatus.DrugStatusEnum.Inactive]: '\u26AB',
    [DrugStatus.DrugStatusEnum.BlockedFromFills]: '\uD83D\uDD34',
};

interface EquivalentOption {
    drugID: number;
    drugSource: number;
    name: string;
}

interface IngredientSlot {
    ingredientId: number;
    originalDrugId: number;
    originalDrugSource: number;
    originalDrugName: string;
    selectedDrugKey: string;
    quantity: number;
    unitOfMeasure: string;
    equivalents: EquivalentOption[];
    loadingEquivalents: boolean;
    loadingDrugName: boolean;
}

@Component({
    name: 'ManagePresetsModal'
})
export default class ManagePresetsModal extends Vue {
    @Prop({ required: true }) readonly modalId!: string;
    @Prop({ required: false, default: null }) readonly currentCompound!: CompoundDrug | null;

    private visible = false;
    private loading = false;
    private saving = false;
    private editingPreset = false;

    private presets: CompoundPreset[] = [];
    private editPreset: { id: number; name: string; isDefault: boolean } = { id: 0, name: '', isDefault: false };
    private ingredientSlots: IngredientSlot[] = [];

    private presetFields = [
        { key: 'name', label: 'Preset Name', sortable: true },
        { key: 'ingredientCount', label: 'Ingredients' },
        { key: 'actions', label: 'Actions', class: 'text-right' }
    ];

    drugKey(drugSource: number, drugId: number): string {
        return `${drugSource}_${drugId}`;
    }

    drugSourceLabel(source: number): string {
        return DrugSource[source]?.toString() || 'Unknown';
    }

    get isNameDuplicate(): boolean {
        const trimmedName = this.editPreset.name?.trim().toLowerCase();
        if (!trimmedName) return false;
        return this.presets.some(
            p => p.name.trim().toLowerCase() === trimmedName && p.id !== this.editPreset.id
        );
    }

    get canSave(): boolean {
        return !!this.editPreset.name?.trim() && this.ingredientSlots.length > 0 && !this.isNameDuplicate;
    }

    async onShow() {
        if (!this.currentCompound?.compoundID) {
            this.$notification(NotificationOptions.error('No compound selected'));
            this.visible = false;
            return;
        }
        await this.loadPresets();
    }

    onHidden() {
        this.presets = [];
        this.editingPreset = false;
        this.ingredientSlots = [];
    }

    async loadPresets() {
        if (!this.currentCompound?.compoundID) return;

        this.loading = true;
        try {
            const response = await Axios.get<CompoundPreset[]>(
                `/Compound/${this.currentCompound.compoundID}/presets`
            );
            this.presets = response.data;
        } catch (error) {
            console.error('Error loading presets:', error);
            this.$notification(NotificationOptions.error('Failed to load presets'));
        } finally {
            this.loading = false;
        }
    }

    onNewPreset() {
        this.editPreset = { id: 0, name: '', isDefault: false };
        this.initIngredientSlots();
        this.editingPreset = true;
    }

    onEditPreset(preset: CompoundPreset) {
        this.editPreset = { id: preset.id, name: preset.name, isDefault: preset.isDefault };
        this.initIngredientSlots(preset.ingredients);
        this.editingPreset = true;
    }

    initIngredientSlots(presetIngredients?: CompoundPresetIngredient[]) {
        const ingredients = this.currentCompound?.ingredients || [];
        this.ingredientSlots = [];

        for (const ingredient of ingredients) {
            const presetIngredient = presetIngredients?.find(
                pi => pi.compoundIngredientID === ingredient.id
            );

            const hasDrugName = !!ingredient.drugName;
            const slot: IngredientSlot = {
                ingredientId: ingredient.id,
                originalDrugId: ingredient.drugID,
                originalDrugSource: ingredient.drugSource,
                originalDrugName: ingredient.drugName || '',
                selectedDrugKey: presetIngredient
                    ? this.drugKey(presetIngredient.drugSource, presetIngredient.drugID)
                    : this.drugKey(ingredient.drugSource, ingredient.drugID),
                quantity: presetIngredient?.quantity ?? ingredient.quantity,
                unitOfMeasure: presetIngredient?.unitOfMeasure ?? ingredient.unitOfMeasure,
                equivalents: [],
                loadingEquivalents: true,
                loadingDrugName: !hasDrugName
            };

            this.ingredientSlots.push(slot);
        }

        // Load drug names and equivalents in background — UI is already visible
        Promise.all(this.ingredientSlots.map(slot => this.loadSlotData(slot)));
    }

    async loadSlotData(slot: IngredientSlot) {
        await Promise.all([
            this.loadDrugNameForSlot(slot),
            this.loadEquivalentsForSlot(slot)
        ]);
    }

    async loadDrugNameForSlot(slot: IngredientSlot) {
        if (!slot.loadingDrugName) return;
        try {
            const response = await Axios.get(`/api/Drug/${slot.originalDrugId}`, {
                params: { drugSource: slot.originalDrugSource }
            });
            slot.originalDrugName = response.data?.productNameShort || response.data?.name || 'Unknown';
        } catch {
            slot.originalDrugName = 'Unknown';
        } finally {
            slot.loadingDrugName = false;
        }
    }

    async loadEquivalentsForSlot(slot: IngredientSlot) {
        slot.loadingEquivalents = true;
        try {
            if (slot.originalDrugSource === DrugSource.GsddFederal) {
                // drugID is a PackageID for GSDD — need ProductID for equivalent lookup
                const drugResponse = await Axios.get(`/api/Drug/${slot.originalDrugId}`, {
                    params: { drugSource: DrugSource.GsddFederal, fullDetail: true }
                });
                const productID = drugResponse.data?.productID;
                if (!productID) { slot.equivalents = []; return; }

                const response = await Axios.get(`/api/Drug/${productID}/Equivalent/${slot.originalDrugId}`, {
                    params: { onlyActive: false }
                });
                const equivalents = response.data || [];
                // Sort: preferred first, then active, then inactive, then blocked
                const sorted = [...equivalents].sort((a: any, b: any) => {
                    const order = (s: number) => s === DrugStatus.DrugStatusEnum.ActiveAndPreferred ? 0
                        : s === DrugStatus.DrugStatusEnum.Active ? 1
                        : s === DrugStatus.DrugStatusEnum.Inactive ? 2 : 3;
                    return order(a.activeStatus) - order(b.activeStatus);
                });
                slot.equivalents = sorted
                    .filter((eq: any) => eq.packageID !== slot.originalDrugId)
                    .map((eq: any) => {
                        const status = statusIndicators[eq.activeStatus] || '';
                        return {
                            drugID: eq.packageID,
                            drugSource: DrugSource.GsddFederal,
                            name: `${status} ${eq.productNameLong || 'Unknown'} - NDC:${eq.ndc || 'N/A'} - PS: ${eq.packageSize || ''}`
                        };
                    });
            } else if (slot.originalDrugSource === DrugSource.CompoundProduct) {
                // Use compound equivalency
                const response = await Axios.get(`/Compound/${slot.originalDrugId}/equivalents`);
                const equivalents = response.data || [];
                slot.equivalents = equivalents.map((eq: any) => {
                    const status = eq.status != null ? (statusIndicators[eq.status] || '') : '';
                    return {
                        drugID: eq.compoundID,
                        drugSource: DrugSource.CompoundProduct,
                        name: `${status} ${eq.name} (${eq.ndc || 'No NDC'})`
                    };
                });
            }
        } catch (error) {
            console.warn(`Error loading equivalents for ingredient ${slot.ingredientId}:`, error);
            slot.equivalents = [];
        } finally {
            slot.loadingEquivalents = false;
        }
    }

    onSlotDrugChanged(_slot: IngredientSlot) {
        // Reactivity is handled by v-model
    }

    async savePreset() {
        if (!this.currentCompound?.compoundID || !this.canSave) return;

        this.saving = true;
        try {
            const presetData: any = {
                id: this.editPreset.id || 0,
                compoundID: this.currentCompound.compoundID,
                name: this.editPreset.name.trim(),
                isDefault: this.editPreset.isDefault,
                ingredients: this.ingredientSlots.map(slot => {
                    const [drugSource, drugID] = slot.selectedDrugKey.split('_').map(Number);
                    return {
                        compoundIngredientID: slot.ingredientId,
                        drugID,
                        drugSource,
                        quantity: slot.quantity,
                        unitOfMeasure: slot.unitOfMeasure
                    } as CompoundPresetIngredient;
                })
            };

            await Axios.post(
                `/Compound/${this.currentCompound.compoundID}/presets`,
                presetData
            );

            this.$notification(NotificationOptions.success(
                `Preset "${this.editPreset.name}" ${this.editPreset.id ? 'updated' : 'created'} successfully`
            ));

            this.editingPreset = false;
            await this.loadPresets();
        } catch (error) {
            console.error('Error saving preset:', error);
            this.$notification(NotificationOptions.error('Failed to save preset'));
        } finally {
            this.saving = false;
        }
    }

    async onDeletePreset(preset: CompoundPreset) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Are you sure you want to delete preset "${preset.name}"?`,
            {
                title: 'Confirm Delete',
                okVariant: 'danger',
                okTitle: 'Delete',
                cancelTitle: 'Cancel',
                centered: true
            }
        );

        if (!confirmed) return;

        try {
            await Axios.delete(`/Compound/presets/${preset.id}`);
            this.$notification(NotificationOptions.success(`Preset "${preset.name}" deleted`));
            await this.loadPresets();
        } catch (error: any) {
            console.error('Error deleting preset:', error);
            const message = error?.response?.data || 'Failed to delete preset';
            this.$notification(NotificationOptions.error(message));
        }
    }

    cancelEdit() {
        this.editingPreset = false;
        this.ingredientSlots = [];
    }
}
</script>

<style scoped>
.b-overlay {
    min-height: 200px;
}
</style>
